<template>
  <Transition name="toast-slide" appear>
    <div
      v-if="visible"
      :class="['custom-toast', `toast-${type}`]"
      @click="handleClick"
    >
      <div class="toast-icon-container">
        <div :class="['toast-icon', `icon-${type}`]">
          <i :class="iconClass"></i>
        </div>
        <div :class="['toast-gradient', `gradient-${type}`]"></div>
      </div>
      <div class="toast-content">
        <div class="toast-message">{{ message }}</div>
        <div v-if="subtitle" class="toast-subtitle">{{ subtitle }}</div>
      </div>
      <button v-if="closable" class="toast-close" @click.stop="close">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  type: {
    type: String,
    default: 'info',
    validator: (value) =>
      ['success', 'error', 'info', 'warning', 'preparing'].includes(value),
  },
  message: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    default: '',
  },
  duration: {
    type: Number,
    default: 4000,
  },
  closable: {
    type: Boolean,
    default: true,
  },
  position: {
    type: String,
    default: 'bottom-left',
    validator: (value) =>
      [
        'top-left',
        'top-right',
        'bottom-left',
        'bottom-right',
        'top-center',
        'bottom-center',
      ].includes(value),
  },
})

const emit = defineEmits(['close', 'click'])

const visible = ref(false)
let timeoutId = null

const iconClass = computed(() => {
  const icons = {
    success: 'fas fa-check',
    error: 'fas fa-times',
    info: 'fas fa-info',
    warning: 'fas fa-exclamation',
    preparing: 'fas fa-spinner fa-spin',
  }
  return icons[props.type] || icons.info
})

const handleClick = () => {
  emit('click')
}

const close = () => {
  visible.value = false
  setTimeout(() => {
    emit('close')
  }, 300) // Wait for animation to complete
}

onMounted(() => {
  visible.value = true

  if (props.duration > 0) {
    timeoutId = setTimeout(() => {
      close()
    }, props.duration)
  }
})

onUnmounted(() => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
})
</script>

<style scoped>
.custom-toast {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  margin: 8px 0;
  min-width: 320px;
  max-width: 480px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.custom-toast:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.toast-icon-container {
  position: relative;
  display: flex;
  align-items: center;
  padding: 16px;
}

.toast-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
  z-index: 2;
  position: relative;
}

.icon-success {
  background: #22c55e;
}

.icon-error {
  background: #ef4444;
}

.icon-info {
  background: #3b82f6;
}

.icon-warning {
  background: #f59e0b;
}

.icon-preparing {
  background: #f97316;
}

.toast-gradient {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 80px;
  border-radius: 12px 0 0 12px;
  z-index: 1;
}

.gradient-success {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%);
}

.gradient-error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%);
}

.gradient-info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%);
}

.gradient-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%);
}

.gradient-preparing {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
}

.toast-content {
  flex: 1;
  padding: 16px 16px 16px 0;
}

.toast-message {
  font-weight: 600;
  font-size: 16px;
  color: #1f2937;
  line-height: 1.4;
}

.toast-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.3;
}

.toast-close {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.toast-close:hover {
  color: #6b7280;
  background: #f3f4f6;
}

/* Animation */
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}

.toast-slide-enter-from {
  opacity: 0;
  transform: translateX(-100%);
}

.toast-slide-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Position variants */
.toast-container {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
}

.toast-container.top-left {
  top: 20px;
  left: 20px;
}

.toast-container.top-right {
  top: 20px;
  right: 20px;
}

.toast-container.bottom-left {
  bottom: 20px;
  left: 20px;
}

.toast-container.bottom-right {
  bottom: 20px;
  right: 20px;
}

.toast-container.top-center {
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
}

.toast-container.bottom-center {
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
}

.toast-container .custom-toast {
  pointer-events: auto;
}
</style>
